import { createEmptyDocument, openDocument, type EditorDocument } from "@zsherman/editor";
import { documentToMarkdown, markdownToDocument } from "@zsherman/editor/markdown";
import type { DocumentRepository } from "@zsherman/editor/persistence";
import type { EditorValue, StoredEditorDocument } from "./types";
import { HQ_URL_POLICY } from "./urlPolicy";

/**
 * The document to open for a value: its stored document, labelled with this editor's id, or one
 * built from its Markdown. Building one is not an edit, so opening a note never writes to it.
 */
export function documentFor(value: EditorValue, documentId: string): EditorDocument {
  const stored = value.document;
  if (stored !== undefined) {
    const labelled = stored.documentId === documentId ? stored : { ...stored, documentId };
    const opened = openDocument(labelled);
    if (opened.status === "ready") return opened.document;
  }
  if (!value.markdown) return createEmptyDocument(documentId);
  return markdownToDocument(value.markdown, documentId, { urlPolicy: HQ_URL_POLICY });
}

/** The Markdown mirror of a document, written without a live editor. */
export function markdownOf(document: EditorDocument): string {
  const result = documentToMarkdown(document, { urlPolicy: HQ_URL_POLICY });
  if (!result.ok) throw new Error(`The note could not be read: ${result.message}`);
  return result.text;
}

export type HostRepository = DocumentRepository & {
  /** The host's value changed underneath the editor: another device, another tab. */
  announce(value: EditorValue): void;
  /** Who is handed each commit: the component, as of its latest render. */
  publishTo(publish: (document: EditorDocument) => Promise<void>): void;
};

/**
 * Storage for one editor's session, backed by the host's props. A commit hands the content to the
 * host at once, and the host's own autosave makes it durable and says when it is: the session's
 * "saved" only means the host has it. Revisions count locally; the host's own revisions guard its
 * writes.
 */
export function createHostRepository(initial: EditorValue): HostRepository {
  let value = initial;
  let revision = 0;
  let listener: ((revision: string) => void) | null = null;
  let publish: ((document: EditorDocument) => Promise<void>) | null = null;
  return {
    load(documentId) {
      const document = documentFor(value, documentId);
      return Promise.resolve({
        status: "ok",
        stored: { document, storageRevision: String(revision) },
      });
    },
    async commit({ document }) {
      if (publish === null) throw new Error("The editor is not connected to its note.");
      await publish(document);
      revision += 1;
      return { status: "committed", storageRevision: String(revision) };
    },
    subscribe(_documentId, next) {
      listener = next;
      return () => {
        if (listener === next) listener = null;
      };
    },
    announce(next) {
      value = next;
      revision += 1;
      listener?.(String(revision));
    },
    publishTo(next) {
      publish = next;
    },
  };
}

/** The envelope as HQ stores it. The shapes are the same; only the content's type is looser. */
export const toStored = (document: EditorDocument): StoredEditorDocument =>
  document as unknown as StoredEditorDocument;
